import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "./AppIcon";
import { useAuth } from "@/lib/AuthProvider";
import { billingNotice, canManageBilling, nextCheck, trialCountdown } from "@/lib/billingNotice";

/**
 * Barra de aviso del estado comercial. Cubre DOS momentos distintos:
 *
 *   · **Preventivo** — el trial vence dentro de 48 h y la cuenta sigue operando (ámbar).
 *   · **Cuenta caída** — trial vencido, pausada, cancelada o en mora (rosa).
 *
 * Nunca se muestran juntos: `billingNotice()` exige `billing_active === false` y `trialCountdown()`
 * exige `=== true`, así que para un mismo snapshot como mucho uno devuelve algo. Eso NO significa
 * que no haya ventana: la capacidad está cacheada y los dos relojes (servidor y navegador) pueden
 * discrepar, así que entre el vencimiento local y su confirmación no se muestra nada. Ver
 * `lib/billingNotice.js`.
 *
 * ── El tiempo pasa con el panel abierto ──
 *
 * Una herramienta de trabajo se deja abierta; cruzar las 48 h, las 24 h o el vencimiento sin
 * recargar es el caso NORMAL. Por eso el reloj vive en el estado y hay un `setTimeout` que despierta
 * en cada hito (`nextCheck()`). Al vencer se REPREGUNTA a la base en vez de voltear el aviso con el
 * reloj local.
 *
 * Vive en `TenantLayout`, o sea que se ve en TODAS las pantallas del panel. Es a propósito: el
 * problema que resuelve es justamente que el corte era invisible salvo que la persona entrara a
 * `/app/billing` por su cuenta. La regla de negocio y el porqué del dato están en
 * `lib/billingNotice.js`.
 *
 * ── Qué NO hace, y por qué ──
 *
 * · **No se puede descartar.** Un banner que se cierra es correcto para una novedad; este describe
 *   un servicio caído que sigue caído después del clic. Esconderlo sería esconder justamente lo
 *   único que hay que accionar. (Si molesta, la salida es resolver la facturación, que es el punto.)
 * · **No aparece en `/app/billing`.** Ahí ya hay un cartel propio y la persona está en el lugar
 *   correcto; repetirlo sólo roba altura.
 * · **No le grita a un agente.** `channel_capacity_self` es owner/admin y le responde `forbidden`
 *   a un `agent`: eso se traga en silencio y no se muestra nada. El estado comercial es de quien
 *   puede resolverlo — mismo criterio que ya tomaron `channel_capacity_self` y el `overview` de
 *   billing, no una decisión nueva de este componente.
 */

/**
 * Paleta por tono. El color es información, no decoración:
 *   · `warn`    — todavía funciona, pero hay una fecha encima (ámbar, `Clock`).
 *   · `down`    — el servicio está caído (rosa, `AlertTriangle`).
 *   · `neutral` — se apagó y no sabemos por qué; no se acusa a nadie de no pagar.
 */
const TONO = {
  warn: {
    icon: "Clock",
    caja: "border-amber-200 bg-amber-50",
    icono: "text-amber-500",
    titulo: "text-amber-900",
    cuerpo: "text-amber-800",
    boton: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  down: {
    icon: "AlertTriangle",
    caja: "border-rose-200 bg-rose-50",
    icono: "text-rose-500",
    titulo: "text-rose-900",
    cuerpo: "text-rose-700",
    boton: "bg-rose-600 hover:bg-rose-700 text-white",
  },
  neutral: {
    icon: "AlertTriangle",
    caja: "border-slate-200 bg-slate-50",
    icono: "text-slate-400",
    titulo: "text-slate-800",
    cuerpo: "text-slate-600",
    boton: "bg-slate-700 hover:bg-slate-800 text-white",
  },
};

/**
 * Previsualización SOLO en desarrollo: `?aviso=trial_expired` fuerza el estado.
 *
 * Sin esto el aviso es imposible de mirar en local, y no por un detalle: `.env` apunta a la base de
 * PRODUCCIÓN, donde el workspace propio está al día. O sea que la barra nunca se dibujaría y la
 * única forma de verla sería romperle la facturación a un tenant real.
 *
 * Queda pegado en `sessionStorage` para poder recorrer TODAS las pantallas con el aviso puesto: si
 * dependiera del query param, se perdería en el primer clic del sidebar y no se podría revisar cómo
 * convive con cada página. `?aviso=real` lo suelta; cerrar la pestaña también.
 *
 * ⚠️ `real` NO quiere decir "sin banner": suelta la simulación y vuelve al dato de la base. Si esa
 * cuenta está de verdad vencida o cancelada, el aviso SIGUE —y con razón—. La primera versión de
 * esto se llamaba `ok`, que se leía como "apagar" y hacía pensar que algo fallaba cuando la barra
 * seguía ahí con el dato verdadero.
 *
 * ⚠️ SOBRE EL BUNDLE DE PRODUCCIÓN — corrigiendo lo que decía acá antes. La versión anterior de
 * este comentario afirmaba que "esto se va entero en el build". Se comprobó contra el bundle
 * servido en producción y **es falso**: la clave de `sessionStorage` estaba ahí. La lectura vivía
 * en el inicializador del `useState`, fuera de toda guarda, así que el minificador no podía
 * descartarla.
 *
 * Lo cierto es esto: en producción `import.meta.env.DEV` compila a `false`, y de ahí que
 * `usePreviewOverride` devuelva `null` SIEMPRE. La previsualización es **inalcanzable**, no
 * necesariamente ausente — ni siquiera seteando la clave a mano en el navegador se activa. Y si se
 * activara, sólo pintaría un cartel: no toca la base, no cambia permisos y no oculta un aviso real
 * (los avisos reales se calculan desde la RPC, no desde acá).
 *
 * Ahora la lectura está detrás de la guarda y el minificador sí descarta el bloque: verificado
 * sobre `build/` — ni `mb_preview_aviso` ni las claves `trial_*` aparecen en el bundle. Aun así, lo
 * que GARANTIZA el comportamiento es el `return null`, no la ausencia del string; si un cambio
 * futuro vuelve a filtrar la constante al bundle, eso es higiene perdida, no una puerta abierta.
 */
const PREVIEW_KEY = "mb_preview_aviso";

/** Trials simulados: `?aviso=trial_36h` → una cuenta viva que vence en 36 h. */
const PREVIEW_TRIAL_HORAS = { trial_48h: 48, trial_36h: 36, trial_24h: 24, trial_2h: 2 };

function usePreviewOverride() {
  const { search } = useLocation();
  const [motivo, setMotivo] = useState(() => {
    if (!import.meta.env.DEV) return null;
    try { return sessionStorage.getItem(PREVIEW_KEY); } catch { return null; }
  });

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const pedido = new URLSearchParams(search).get("aviso");
    if (!pedido) return;
    const soltar = pedido === "real" || pedido === "ok"; // `ok` se mantiene por costumbre
    try {
      if (soltar) sessionStorage.removeItem(PREVIEW_KEY);
      else sessionStorage.setItem(PREVIEW_KEY, pedido);
    } catch { /* modo privado: la previsualización dura lo que dure la URL */ }
    setMotivo(soltar ? null : pedido);
  }, [search]);

  if (!import.meta.env.DEV || !motivo) return null;

  const horas = PREVIEW_TRIAL_HORAS[motivo];
  if (horas !== undefined) {
    return {
      billing_active: true,
      billing_status: "trial",
      trial_ends_at: new Date(Date.now() + horas * 60 * 60 * 1000).toISOString(),
    };
  }
  return { billing_active: false, billing_reason: motivo };
}

export default function BillingNotice() {
  const { t } = useTranslation("billing");
  const { supabase, tenant, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [capacity, setCapacity] = useState(null);
  // El reloj vive en el estado. Si se leyera `Date.now()` suelto en el render, el paso del tiempo
  // no despertaría a React y el aviso llegaría recién con el próximo cambio de pantalla.
  const [ahora, setAhora] = useState(() => Date.now());
  // Se incrementa para forzar una relectura de la RPC (el vencimiento lo confirma el servidor).
  const [relectura, setRelectura] = useState(0);
  const preview = usePreviewOverride();

  useEffect(() => {
    let vigente = true;
    if (!supabase || !tenant?.id) { setCapacity(null); return undefined; }

    (async () => {
      // Un `agent`/`viewer` recibe `forbidden` acá. No es un error a reportar: es el contrato.
      const { data, error } = await supabase.rpc("channel_capacity_self", { p_tenant_id: tenant.id });
      if (!vigente) return;
      setCapacity(error ? null : data);
      setAhora(Date.now()); // el reloj se sincroniza con la respuesta, no con el montaje
    })();

    return () => { vigente = false; };
  }, [supabase, tenant?.id, relectura]);

  // ── Despertar en el próximo hito ───────────────────────────────────────────────────────────
  //
  // La decisión de CUÁNDO vive en `nextCheck()`, que es pura y está testeada aparte. Acá sólo se
  // arma el `setTimeout` y se limpia. Al reprogramarse sobre `ahora`, cada despertar encadena el
  // siguiente: 48 h → 24 h → vencimiento.
  useEffect(() => {
    const plan = nextCheck(capacity, ahora);
    if (!plan) return undefined;

    const id = setTimeout(() => {
      setAhora(Date.now());
      // Al vencer no se voltea el aviso con el reloj local: se le pregunta a la base. Ver
      // `nextCheck()` — es lo que evita anunciar un corte que el servidor no hizo.
      if (plan.refetch) setRelectura((n) => n + 1);
    }, plan.delayMs);

    return () => clearTimeout(id);
  }, [capacity, ahora]);

  // Para un mismo snapshot como mucho uno de los dos devuelve algo (ver `lib/billingNotice.js`),
  // así que el `??` encadena sin elegir precedencia. `ahora` se pasa explícito: es lo que hace que
  // el paso del tiempo sea un cambio de estado y no una lectura escondida en el render.
  const fuente = preview ?? capacity;
  const aviso = billingNotice(fuente) ?? trialCountdown(fuente, ahora);

  // En `/app/billing` no: la pantalla ya lo dice y la persona ya está donde tiene que estar.
  if (!aviso || location.pathname.startsWith("/app/billing")) return null;

  const paleta = TONO[aviso.tone] ?? TONO.neutral;
  const puedeGestionar = canManageBilling(profile?.role);
  // El CTA a facturación sólo se ofrece a quien puede entrar ahí. A un admin sin acceso de pago,
  // un botón que lo lleva a una pantalla vacía es peor que ningún botón.
  const mostrarCta = aviso.cta === "billing" && puedeGestionar;

  return (
    <div
      role="status"
      className={`shrink-0 border-b px-4 py-3 sm:px-6 ${paleta.caja}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <Icon name={paleta.icon} size={18} className={`mt-0.5 shrink-0 ${paleta.icono}`} />
          <div className="min-w-0">
            {/* `count` sólo lo usa el copy de la cuenta regresiva; al resto i18next le ignora el extra. */}
            <p className={`text-sm font-bold ${paleta.titulo}`}>
              {t(`notice.${aviso.copy}.title`, { count: aviso.days })}
            </p>
            <p className={`mt-0.5 text-xs leading-relaxed ${paleta.cuerpo}`}>
              {t(`notice.${aviso.copy}.body`, { count: aviso.days })}
            </p>
          </div>
        </div>

        {mostrarCta && (
          <button
            type="button"
            onClick={() => navigate("/app/billing")}
            className={`inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-bold transition-colors sm:w-auto ${paleta.boton}`}
          >
            {t("notice.cta.billing")}
            <Icon name="ArrowRight" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
